import { useMemo, type ReactNode } from "react";
import { useGetAllArtworks } from "@/api/artwork/artwork";
import { useGetAllMultiplePrints } from "@/api/multipleprint/multipleprint";
import { useSections } from "@/api/cms";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { CatalogueDecoupagesFiltersPreview } from "@/components/cms-puck/previews/CatalogueDecoupagesFiltersPreview";
import { useAuth } from "@/context/auth/AuthContext";
import type { CatalogueRaisonneSection } from "@/lib/cms/cms-types";
import type { SupportedLocale } from "@/i18n";
import { getDefaultAttachment } from "@/lib/image";
import { ArtworkMinifySummaryCF } from "@/lib/compute-field/artwork";
import type { Artwork, Multipleprint } from "@/lib/types";
import { useTranslation } from "react-i18next";

import { CMS_FLOW_CLASS } from "@/lib/cms-puck/block-style/defaults";
import { contentColumnsGridAttrs } from "@/lib/cms-puck/blocks/column-count";
import { normalizeGalleryColumns } from "@/lib/cms-puck/blocks/gallery";

/** Même vignette que le front (`ArtCard`) : image par défaut, taille medium. */
function artworkImageUrl(artwork: Artwork): string | undefined {
  const att = artwork.attachments?.length
    ? getDefaultAttachment(artwork.attachments, "artwork")
    : null;
  return att?.mediumPublicUrl ?? att?.publicUrl ?? att?.largePublicUrl;
}

type CardImage = {
  mediumPublicUrl?: string;
  publicUrl?: string;
  largePublicUrl?: string;
};

/**
 * Une estampe vue comme une œuvre pour le résumé de carte : mêmes
 * correspondances que la vue `CatalogListItem` qui alimente le front.
 */
function multipleprintSummaryInput(m: Multipleprint) {
  return {
    itemType: "MULTIPLE",
    oeuvresDescriptionTitre: m.estampesMultiplesDescriptionTitre,
    oeuvresDescriptionTitreMultilingual: {},
    oeuvresDescriptionTitreMultilingualPublished: true,
    oeuvresDescriptionDateDeCreation:
      m.estampesMultiplesDescriptionDateDeCreation,
    oeuvresDescriptionLieuDeCreation: "",
    oeuvresDescriptionTechnique: m.estampesMultiplesDescriptionTechnique ?? {},
    oeuvresDescriptionSupport:
      m.estampesMultiplesDescriptionSupportMateriau ?? {},
    oeuvresDescriptionOeuvreDoubleFace: false,
    oeuvresDescriptionDimensionsOeuvre:
      m.estampesMultiplesDescriptionDimensionsOeuvre,
    oeuvresDescriptionDimensionsOeuvreInconnues: false,
    oeuvresDescriptionDimensionsOeuvreRaw:
      m.estampesMultiplesDescriptionDimensionsOeuvreRaw,
  };
}

/** Filtre Prisma approximatif pour l'aperçu éditeur (parité partielle front). */
function buildPreviewWhereForSection(
  section: CatalogueRaisonneSection | undefined,
  target: "ARTWORKS" | "MULTIPRINTS",
): object {
  const published =
    target === "ARTWORKS"
      ? { oeuvresPublicationStatus: { equals: "published" } }
      : { estampesMultiplesPublicationStatus: { equals: "published" } };

  if (!section?.type) return published;

  const and: object[] = [published];

  switch (section.type) {
    case "creationDate": {
      const start = parseInt(section.startValue ?? "", 10);
      const end = parseInt(section.endValue ?? "", 10);
      if (!Number.isNaN(start) && !Number.isNaN(end) && start <= end) {
        const years: string[] = [];
        for (let y = start; y <= end; y += 1) years.push(String(y));
        if (years.length > 0) {
          const field =
            target === "ARTWORKS"
              ? "oeuvresDescriptionDateDeCreation"
              : "estampesMultiplesDescriptionDateDeCreation";
          and.push({
            OR: years.map((y) => ({
              [field]: { contains: y, mode: "insensitive" },
            })),
          });
        }
      }
      break;
    }
    case "categories":
      if (section.categoryId) {
        and.push(
          target === "ARTWORKS"
            ? {
                oeuvresDescriptionCategorie: {
                  contains: section.categoryId,
                  mode: "insensitive",
                },
              }
            : {
                estampesMultiplesDescriptionCategorie: {
                  contains: section.categoryId,
                  mode: "insensitive",
                },
              },
        );
      }
      break;
    case "series":
      if (section.categoryId && target === "ARTWORKS") {
        and.push({
          oeuvresDescriptionSerie: {
            contains: section.categoryId,
            mode: "insensitive",
          },
        });
      }
      break;
    case "themes":
      if (section.categoryId && target === "ARTWORKS") {
        and.push({
          oeuvresDescriptionTheme: {
            contains: section.categoryId,
            mode: "insensitive",
          },
        });
      }
      break;
    case "registrationNumber": {
      const start = section.startValue?.trim();
      const end = section.endValue?.trim();
      if (start || end) {
        const field =
          target === "ARTWORKS"
            ? "oeuvresDescriptionNumeroEnregistrement"
            : "estampesMultiplesDescriptionNumeroEnregistrement";
        if (start && end && start === end) {
          and.push({ [field]: { equals: start } });
        } else if (start) {
          and.push({ [field]: { gte: start } });
        }
        if (end && start !== end) {
          and.push({ [field]: { lte: end } });
        }
      }
      break;
    }
    default:
      break;
  }

  return and.length === 1 ? published : { AND: and };
}

function ListingPreviewCard({
  title,
  imageUrl,
  registrationNumber,
  showRegistrationNumber,
  summary,
}: {
  title: string;
  imageUrl?: string;
  registrationNumber?: string;
  showRegistrationNumber?: boolean;
  /** Résumé complet (parité front) ; sinon le titre seul. */
  summary?: ReactNode;
}) {
  const vt = useVisibleT();
  // Parité `ArtCard` (ancien CMS / front) : box carrée desktop + object-contain
  // (letterbox, pas de crop) — pas de `object-cover` / ratio 4/3.
  return (
    <div className="h-fit">
      <div className="relative flex w-full items-center justify-center overflow-hidden md:aspect-square">
        {imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={imageUrl}
            alt={title}
            className="block h-auto max-h-[70vh] w-full max-w-full object-contain object-center md:max-h-full"
          />
        ) : (
          <div className="flex aspect-square w-full items-center justify-center px-2 text-center text-xs cms-page-text-muted md:aspect-auto md:h-full">
            {title ||
              vt("cms.new.preview.catalogueListing.cardFallback", "Œuvre")}
          </div>
        )}
      </div>
      <div className="pt-2 text-center">
        {showRegistrationNumber && registrationNumber ? (
          <p className="mb-1 text-xs cms-page-text-muted">
            {registrationNumber}
          </p>
        ) : null}
        {summary ?? (
          <p className="font-semibold">
            {title ||
              vt("cms.new.preview.catalogueListing.cardUntitled", "Sans titre")}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Aperçu canvas du bloc « Listing d'un découpage » — parité visuelle avec
 * `PuckCatalogListing` (grille ; filtres = bloc `CatalogueFilters` séparé).
 */
export function CatalogueListingPreview({
  target = "ARTWORKS",
  columns = 4,
  showRegistrationNumber = false,
  showFilters = false,
  previewDecoupageId = null,
}: {
  target?: "ARTWORKS" | "MULTIPRINTS";
  columns?: number;
  showRegistrationNumber?: boolean;
  /** Réservé aux résultats de recherche ; listing = false (filtres séparés). */
  showFilters?: boolean;
  previewDecoupageId?: string | null;
}) {
  const vt = useVisibleT();
  const { instanceData } = useAuth();
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const locale = language as SupportedLocale;
  const cmsId = instanceData?.CMS?.id ?? "";
  const cols = normalizeGalleryColumns(columns);
  const grid = contentColumnsGridAttrs(cols);

  const { data: sections = [] } = useSections(cmsId, target);
  const section = useMemo(() => {
    if (previewDecoupageId) {
      return sections.find((s) => s.id === previewDecoupageId);
    }
    return sections[0];
  }, [previewDecoupageId, sections]);

  const where = useMemo(
    () => buildPreviewWhereForSection(section, target),
    [section, target],
  );

  const { data: artworks = [], isLoading: artworksLoading } = useGetAllArtworks(
    {
      where:
        target === "ARTWORKS"
          ? where
          : { oeuvresPublicationStatus: { equals: "published" } },
      order: [{ oeuvresDescriptionDateDeCreation: "asc" }],
      take: 8,
      skip: 0,
    },
  );

  const { data: multipleprints = [], isLoading: multiplesLoading } =
    useGetAllMultiplePrints({
      where:
        target === "MULTIPRINTS"
          ? where
          : { estampesMultiplesPublicationStatus: { equals: "published" } },
      take: 8,
      skip: 0,
      order: { estampesMultiplesDescriptionNumeroEnregistrement: "asc" },
    });

  const isLoading = target === "ARTWORKS" ? artworksLoading : multiplesLoading;

  const cards: {
    id: string;
    title: string;
    imageUrl?: string;
    registrationNumber?: string;
    summary?: ReactNode;
  }[] =
    target === "ARTWORKS"
      ? artworks.map((art) => ({
          id: art.id,
          title: art.oeuvresDescriptionTitre,
          imageUrl: artworkImageUrl(art),
          registrationNumber: art.oeuvresDescriptionNumeroEnregistrement,
          summary: (
            <ArtworkMinifySummaryCF
              input={art}
              language={locale}
              translation={t}
            />
          ),
        }))
      : multipleprints.map((m) => {
          const att: CardImage | null | undefined = m.attachments?.length
            ? getDefaultAttachment(m.attachments, "multipleprint")
            : (m as { attachment?: CardImage }).attachment;
          return {
            id: m.id,
            title: m.estampesMultiplesDescriptionTitre,
            imageUrl:
              att?.mediumPublicUrl ?? att?.publicUrl ?? att?.largePublicUrl,
            registrationNumber:
              m.estampesMultiplesDescriptionNumeroEnregistrement,
            summary: (
              <ArtworkMinifySummaryCF
                input={multipleprintSummaryInput(m)}
                language={locale}
                translation={t}
              />
            ),
          };
        });

  return (
    <div className="w-full">
      {showFilters ? (
        <CatalogueDecoupagesFiltersPreview target={target} />
      ) : null}
      {isLoading ? (
        <p className="py-6 text-center text-sm cms-page-text-muted">
          {vt("cms.new.preview.catalogueListing.loading", "Chargement du listing…")}
        </p>
      ) : cards.length === 0 ? (
        <p className="py-6 text-center text-sm cms-page-text-muted">
          {vt(
            "cms.new.preview.catalogueListing.empty",
            "Aucune œuvre publiée à afficher dans l'aperçu.",
          )}
        </p>
      ) : (
        // `CMS_FLOW_CLASS` : cible des surcharges Disposition tablette / mobile.
        // mt-5 : parité front (espace sous les filtres, listing ET recherche).
        <div
          className={`${CMS_FLOW_CLASS} ${grid.className} mt-5 gap-8`}
          style={{ ...grid.style, gridAutoRows: "min-content" }}
        >
          {cards.map((card) => (
            <ListingPreviewCard
              key={card.id}
              title={card.title}
              imageUrl={card.imageUrl}
              registrationNumber={card.registrationNumber}
              showRegistrationNumber={showRegistrationNumber}
              summary={card.summary}
            />
          ))}
        </div>
      )}
    </div>
  );
}
